// HeroDiscovery.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import ProductCard from './ProductCard';
import { fetchExclusiveFeed, registerShownProducts, resetFeedRegistry, completeHeroGate } from '../utils/feedRegistry';
import '../StylesComponents/hero-discovery.css';

// Showcase order — one strong product each, recognition first.
// The product for each brand is chosen server-side from a quality pool and
// rotates once per calendar day (Australia/Sydney) so the carousel stays fresh.
const HERO_BRANDS = [
  { name: 'Nike', slug: 'nike' },
  { name: 'Adidas', slug: 'adidas' },
  { name: 'New Balance', slug: 'new-balance' },
  { name: 'ASICS', slug: 'asics' },
  { name: 'The North Face', slug: 'the-north-face' },
  { name: 'Carhartt', slug: 'carhartt' },
];

const SHUFFLE_MS = 2800;
const SHUFFLE_START_DELAY_MS = 1200;
const QUEUE_ADVANCE_MS = 3400;
const QUEUE_ADVANCE_START_MS = 1600;

function swapRandomPairs(order) {
  const next = [...order];
  const n = next.length;
  if (n < 2) return next;

  const swaps = n >= 4 ? 2 : 1;
  const used = new Set();

  for (let s = 0; s < swaps; s += 1) {
    let i = Math.floor(Math.random() * n);
    let guard = 0;
    while (used.has(i) && guard < 8) {
      i = Math.floor(Math.random() * n);
      guard += 1;
    }

    let j = Math.floor(Math.random() * n);
    guard = 0;
    while ((j === i || used.has(j)) && guard < 8) {
      j = Math.floor(Math.random() * n);
      guard += 1;
    }

    if (i === j) continue;
    used.add(i);
    used.add(j);
    [next[i], next[j]] = [next[j], next[i]];
  }

  return next;
}

// rotation[0] = featured; queue left→right = just-shown … next-up (rightmost)
function advanceQueue(rotation) {
  if (rotation.length < 2) return rotation;
  const [featured, ...queue] = rotation;
  const next = queue[queue.length - 1];
  const rest = queue.slice(0, -1);
  return [next, featured, ...rest];
}

function promoteInQueue(rotation, slotIndex) {
  if (!rotation.includes(slotIndex) || rotation[0] === slotIndex) return rotation;
  const [featured, ...queue] = rotation;
  const rest = queue.filter((i) => i !== slotIndex);
  return [slotIndex, featured, ...rest];
}

function BrandTag({ brand, slotIndex = 0 }) {
  const variant = slotIndex % 3;
  return (
    <div
      className={`floating-deal-brand-tag floating-deal-brand-tag--v${variant} floating-deal-brand-tag--s${slotIndex}`}
      style={{ '--brand-i': slotIndex }}
    >
      <span className="floating-deal-brand-label">{brand.name}</span>
    </div>
  );
}

function HeroBrandCard({ brand, product, slotIndex, featured, onHoverPause }) {
  return (
    <div
      className={`floating-deal-stack floating-deal-stack--${slotIndex}${featured ? ' is-featured' : ''}`}
      onMouseEnter={() => onHoverPause(true)}
      onMouseLeave={() => onHoverPause(false)}
      onTouchStart={() => onHoverPause(true)}
      onTouchEnd={() => onHoverPause(false)}
    >
      <BrandTag brand={brand} slotIndex={slotIndex} />
      <div className="floating-deal-card-shell">
        <ProductCard
          product={product}
          theme="light"
          feedSection="hero_showcase"
          position={slotIndex}
        />
      </div>
    </div>
  );
}

function HeroQueueThumb({ brand, product, onSelect, style }) {
  const image = product.image || (Array.isArray(product.images) && product.images[0]) || '/images/default-product.jpg';

  return (
    <button
      type="button"
      className="hero-queue-thumb"
      style={style}
      onClick={onSelect}
      aria-label={`Showcase ${brand.name}`}
    >
      <span className="hero-queue-thumb-image-wrap">
        <img src={image} alt="" className="hero-queue-thumb-image" loading="lazy" />
      </span>
      <span className="hero-queue-thumb-brand">{brand.name}</span>
    </button>
  );
}

function HeroDiscovery() {
  const [slots, setSlots] = useState([]);
  // Desktop 3×2 cell assignment for each slots[i]
  const [layout, setLayout] = useState([]);
  // Compact layout: rotation[0] = featured, rotation[1..] = queue under blurb
  const [rotation, setRotation] = useState([]);
  const [featureKey, setFeatureKey] = useState(0);
  const [queueEpoch, setQueueEpoch] = useState(0);
  const pausedRef = useRef(false);
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  // Reset before sibling effects so post-hero sections wait on a fresh gate.
  const didResetRegistry = useRef(false);
  if (!didResetRegistry.current) {
    didResetRegistry.current = true;
    resetFeedRegistry();
  }

  // Soft background grid + particles (fainter take on Shop by Budget)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const motionOk = window.matchMedia('(prefers-reduced-motion: no-preference)');
    let animationFrameId;
    let particles = [];

    const resize = () => {
      if (canvas.parentElement) {
        canvas.width = canvas.parentElement.offsetWidth;
        canvas.height = canvas.parentElement.offsetHeight;
      }
    };

    const seedParticles = () => {
      particles = [];
      const count = 36;
      for (let i = 0; i < count; i += 1) {
        const type = Math.random();
        let pSymbol = null;
        if (type > 0.55) {
          if (Math.random() > 0.5) pSymbol = '$';
          else if (Math.random() > 0.5) pSymbol = '%';
          else pSymbol = 'OFF';
        }
        particles.push({
          x: Math.random() * (canvas.width || 1),
          y: Math.random() * (canvas.height || 1),
          size: Math.random() * 2.2 + 1.2,
          speedX: Math.random() * 0.45 - 0.225,
          speedY: Math.random() * 0.55 + 0.18,
          opacity: Math.random() * 0.28 + 0.18,
          symbol: pSymbol,
        });
      }
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const draw = () => {
      if (!motionOk.matches) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = 'rgba(0, 230, 118, 0.1)';
      ctx.lineWidth = 1;
      const gridSize = 88;
      const scrollOffset = (Date.now() / 70) % gridSize;

      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = scrollOffset; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i += 1) {
        for (let j = i + 1; j < particles.length; j += 1) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.strokeStyle = `rgba(0, 230, 118, ${0.22 * (1 - dist / 150)})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        const mdx = p.x - mouseRef.current.x;
        const mdy = p.y - mouseRef.current.y;
        const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
        if (mdist < 200 && mdist > 0.01) {
          const force = (200 - mdist) / 200;
          p.x += (mdx / mdist) * force * 3.5;
          p.y += (mdy / mdist) * force * 3.5;
        }

        if (p.symbol) {
          ctx.fillStyle = `rgba(0, 230, 118, ${p.opacity + 0.12})`;
          ctx.font = p.symbol === 'OFF' ? 'bold 9px monospace' : 'bold 14px monospace';
          ctx.fillText(p.symbol, p.x, p.y);
        } else {
          ctx.fillStyle = `rgba(0, 230, 118, ${p.opacity + 0.15})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }

        p.y += p.speedY;
        p.x += p.speedX;

        if (p.y > canvas.height) p.y = -20;
        if (p.x > canvas.width) p.x = 0;
        if (p.x < 0) p.x = canvas.width;
      });

      animationFrameId = requestAnimationFrame(draw);
    };

    const onMotionChange = () => {
      cancelAnimationFrame(animationFrameId);
      if (motionOk.matches) {
        animationFrameId = requestAnimationFrame(draw);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    motionOk.addEventListener('change', onMotionChange);
    resize();
    seedParticles();
    if (motionOk.matches) {
      animationFrameId = requestAnimationFrame(draw);
    }

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      motionOk.removeEventListener('change', onMotionChange);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const picks = await fetchExclusiveFeed(async (exclude) => {
          const excludeParam = exclude.length > 0 ? { exclude: exclude.join(',') } : {};
          const results = await Promise.all(
            HERO_BRANDS.map(async (brand) => {
              try {
                const res = await api.get('/api/products', {
                  params: {
                    limit: 1,
                    feedContext: 'hero_showcase',
                    brand: brand.name,
                    ...excludeParam,
                  },
                });
                const product = (res.data.products || [])[0] || null;
                return { brand, product };
              } catch (e) {
                console.error(`Hero brand fetch error (${brand.name}):`, e);
                return { brand, product: null };
              }
            })
          );
          return results;
        });

        const filled = picks.filter((slot) => slot.product);
        const order = filled.map((_, i) => i);
        registerShownProducts(filled.map((slot) => slot.product));
        setSlots(filled);
        setLayout(order);
        setRotation(order);
      } catch (e) {
        console.error('Hero discovery fetch error:', e);
      } finally {
        completeHeroGate();
      }
    };
    fetchDeals();
  }, []);

  // Desktop: shuffle cards between the fixed 3×2 slots
  useEffect(() => {
    if (slots.length < 2) return undefined;

    const desktop = window.matchMedia('(min-width: 1025px)');
    const motionOk = window.matchMedia('(prefers-reduced-motion: no-preference)');

    let intervalId = null;
    let startTimer = null;

    const clearTimers = () => {
      if (startTimer) clearTimeout(startTimer);
      if (intervalId) clearInterval(intervalId);
      startTimer = null;
      intervalId = null;
    };

    const tick = () => {
      if (pausedRef.current) return;
      if (!desktop.matches || !motionOk.matches) return;
      setLayout((prev) => swapRandomPairs(prev));
    };

    const arm = () => {
      clearTimers();
      if (!desktop.matches || !motionOk.matches) return;
      startTimer = setTimeout(() => {
        tick();
        intervalId = setInterval(tick, SHUFFLE_MS);
      }, SHUFFLE_START_DELAY_MS);
    };

    arm();
    desktop.addEventListener('change', arm);
    motionOk.addEventListener('change', arm);

    return () => {
      clearTimers();
      desktop.removeEventListener('change', arm);
      motionOk.removeEventListener('change', arm);
    };
  }, [slots.length]);

  // Mobile / tablet: advance queue — front of line becomes featured, old featured rejoins end
  useEffect(() => {
    if (slots.length < 2) return undefined;

    const compact = window.matchMedia('(max-width: 1024px)');
    const motionOk = window.matchMedia('(prefers-reduced-motion: no-preference)');

    let intervalId = null;
    let startTimer = null;

    const clearTimers = () => {
      if (startTimer) clearTimeout(startTimer);
      if (intervalId) clearInterval(intervalId);
      startTimer = null;
      intervalId = null;
    };

    const tick = () => {
      if (pausedRef.current) return;
      if (!compact.matches || !motionOk.matches) return;
      setRotation((prev) => advanceQueue(prev));
      setFeatureKey((k) => k + 1);
    };

    const arm = () => {
      clearTimers();
      if (!compact.matches || !motionOk.matches) return;
      startTimer = setTimeout(() => {
        tick();
        intervalId = setInterval(tick, QUEUE_ADVANCE_MS);
      }, QUEUE_ADVANCE_START_MS);
    };

    arm();
    compact.addEventListener('change', arm);
    motionOk.addEventListener('change', arm);

    return () => {
      clearTimers();
      compact.removeEventListener('change', arm);
      motionOk.removeEventListener('change', arm);
    };
  }, [slots.length, queueEpoch]);

  const featuredSlotIndex = rotation[0];
  const queueSlotIndexes = rotation.slice(1);

  const handlePromote = (slotIndex) => {
    setRotation((prev) => promoteInQueue(prev, slotIndex));
    setFeatureKey((k) => k + 1);
    setQueueEpoch((e) => e + 1);
    pausedRef.current = false;
  };

  return (
    <section className="hero-discovery">
      <canvas ref={canvasRef} className="hero-discovery-canvas" aria-hidden="true" />
      <div className="hero-discovery-inner">
        <div className="hero-discovery-lead">
          <div className="hero-discovery-copy">
            <p className="hero-discovery-eyebrow">STOCKROOM MARKETPLACE</p>
            <h1 className="hero-discovery-title">
              Premium brands.
              <br />
              Unexpected prices.
            </h1>
            <p className="hero-discovery-subtitle">
              Discover thousands of discounted fashion products from your favourite brands.
            </p>
          </div>

          {queueSlotIndexes.length > 0 && (
            <div
              className="hero-queue"
              aria-label="Upcoming showcase products"
              onMouseEnter={() => { pausedRef.current = true; }}
              onMouseLeave={() => { pausedRef.current = false; }}
              onTouchStart={() => { pausedRef.current = true; }}
              onTouchEnd={() => { pausedRef.current = false; }}
            >
              {queueSlotIndexes.map((slotIndex, queuePos) => {
                const slot = slots[slotIndex];
                if (!slot) return null;
                return (
                  <HeroQueueThumb
                    key={`${slot.brand.slug}-${featureKey}-${queuePos}`}
                    brand={slot.brand}
                    product={slot.product}
                    style={{ '--queue-i': queuePos }}
                    onSelect={() => handlePromote(slotIndex)}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div
          className="hero-discovery-showcase"
          aria-hidden={slots.length === 0}
        >
          {Array.from({ length: Math.max(slots.length, 6) }).map((_, i) => (
            <div key={`sizer-${i}`} className="hero-grid-sizer" aria-hidden="true" />
          ))}

          {slots.map((slot, index) => {
            const isFeatured = index === featuredSlotIndex;
            return (
              <HeroBrandCard
                key={isFeatured ? `feat-${slot.brand.slug}-${featureKey}` : slot.brand.slug}
                brand={slot.brand}
                product={slot.product}
                slotIndex={layout[index] ?? index}
                featured={isFeatured}
                onHoverPause={(paused) => { pausedRef.current = paused; }}
              />
            );
          })}
        </div>

        <div className="hero-discovery-actions">
          <Link to="/all-brands" className="hero-discovery-btn hero-discovery-btn--primary">
            Shop today's deals
          </Link>
        </div>
      </div>
    </section>
  );
}

export default HeroDiscovery;
